import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (value) => {
  return `₹${(value || 0).toLocaleString()}`;
};

const paymentModes = {
  "upi": "UPI",
  "bank_transfer": "Bank Transfer",
  "credit_card": "Credit Card",
  "debit_card": "Debit Card",
  "cash": "Cash"
};

const AgentPayments = () => {
  const { token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState("all"); // all | unpaid | partial | paid
  const [expandedBookingId, setExpandedBookingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payRes, bookRes] = await Promise.all([
        axios.get(`${API}/agent/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPayments(payRes.data || []);
      setBookings(bookRes.data || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case "upi": return "bg-purple-100 text-purple-700";
      case "bank_transfer": return "bg-blue-100 text-blue-700";
      case "credit_card": return "bg-emerald-100 text-emerald-700";
      case "debit_card": return "bg-cyan-500/20 text-cyan-400";
      case "cash": return "bg-amber-100 text-amber-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  // Total Booking Amount = sum of ALL non-cancelled bookings' DMC base.
  // We prefer the freshly-recomputed total returned on payments (it always
  // reflects the latest DMC base from the linked quote). Fall back to the
  // booking record's stored `total_amount` for bookings without payments.
  const latestTotalByBooking = {};
  payments.forEach(p => {
    const key = p.booking_id;
    if (!key) return;
    // Last write wins; payments are sorted DESC by date so first one is freshest.
    if (!(key in latestTotalByBooking)) latestTotalByBooking[key] = Number(p.total_booking_amount || 0);
  });
  const nonCancelled = bookings.filter(b => (b.status || '').toLowerCase() !== 'cancelled');
  const totalBookingAmount = nonCancelled.reduce((sum, b) => {
    const fromPay = latestTotalByBooking[b.id];
    return sum + Number(fromPay !== undefined ? fromPay : (b.total_amount || 0));
  }, 0);
  const cancelledIds = new Set(bookings.filter(b => (b.status || '').toLowerCase() === 'cancelled').map(b => b.id));
  const totalReceived = payments
    .filter(p => !cancelledIds.has(p.booking_id))
    .reduce((sum, p) => sum + (p.amount_received || 0), 0);
  const totalBalance = Math.max(0, totalBookingAmount - totalReceived);

  // Status of each booking based on running paid vs total
  const paidByBooking = {};
  payments.forEach(p => {
    const k = p.booking_id;
    if (!k) return;
    paidByBooking[k] = (paidByBooking[k] || 0) + Number(p.amount_received || 0);
  });
  const totalByBooking = {};
  bookings.forEach(b => {
    const fromPay = latestTotalByBooking[b.id];
    totalByBooking[b.id] = Number(fromPay !== undefined ? fromPay : (b.total_amount || 0));
  });
  const bookingPaymentStatus = (bid) => {
    const total = totalByBooking[bid] || 0;
    const paid = paidByBooking[bid] || 0;
    if (total <= 0 || paid <= 0) return "unpaid";
    if (paid >= total) return "paid";
    return "partial";
  };
  const filteredPayments = paymentFilter === "all"
    ? payments
    : payments.filter(p => {
        if (cancelledIds.has(p.booking_id)) return false;
        return bookingPaymentStatus(p.booking_id) === paymentFilter;
      });
  const filterCounts = {
    all: payments.length,
    unpaid: payments.filter(p => !cancelledIds.has(p.booking_id) && bookingPaymentStatus(p.booking_id) === "unpaid").length,
    partial: payments.filter(p => !cancelledIds.has(p.booking_id) && bookingPaymentStatus(p.booking_id) === "partial").length,
    paid: payments.filter(p => !cancelledIds.has(p.booking_id) && bookingPaymentStatus(p.booking_id) === "paid").length,
  };

  // Cumulative balance per payment row (running deduction within each booking ordered by date+id ASC).
  const balanceById = {};
  const grouped = payments.reduce((acc, p) => {
    const key = p.booking_id || p.booking_ref || p.id;
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});
  Object.values(grouped).forEach(list => {
    const sorted = [...list].sort((a, b) => {
      const da = new Date(a.payment_date || a.created_at || 0).getTime();
      const db = new Date(b.payment_date || b.created_at || 0).getTime();
      if (da !== db) return da - db;
      return String(a.id).localeCompare(String(b.id));
    });
    const total = (sorted[0] && sorted[0].total_booking_amount) || 0;
    let running = 0;
    sorted.forEach(p => {
      running += Number(p.amount_received || 0);
      balanceById[p.id] = Math.max(0, total - running);
    });
  });

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-payments">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">My Payments</h1>
          <p className="text-gray-500">View your payment history and balances</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-gray-500 text-sm">Total Booking Amount</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalBookingAmount)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-gray-500 text-sm">Total Received Amount</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(totalReceived)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-gray-500 text-sm">Total Balance Amount</p>
              <p className={`text-2xl font-bold ${totalBalance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {totalBalance > 0 ? formatCurrency(totalBalance) : 'Nil'}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {/* Filter chips */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-wrap" data-testid="agent-payments-filter-chips">
              {[
                { key: "all",     label: "All",     color: "bg-gray-800 text-white",    inactive: "bg-gray-100 text-gray-700" },
                { key: "unpaid",  label: "Unpaid",  color: "bg-rose-600 text-white",    inactive: "bg-rose-50 text-rose-700" },
                { key: "partial", label: "Partial", color: "bg-amber-500 text-white",   inactive: "bg-amber-50 text-amber-700" },
                { key: "paid",    label: "Paid",    color: "bg-emerald-600 text-white", inactive: "bg-emerald-50 text-emerald-700" },
              ].map(c => {
                const active = paymentFilter === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setPaymentFilter(c.key)}
                    data-testid={`agent-payments-filter-${c.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? c.color + ' shadow-sm' : c.inactive + ' hover:brightness-95'}`}
                  >
                    {c.label}
                    <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-white/70'}`}>
                      {filterCounts[c.key]}
                    </span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500">No payment records found</p>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-gray-500">No payments match this filter.</p>
              </div>
            ) : (
              (() => {
                // Group payments by booking_id
                const bookingMap = {};
                filteredPayments.forEach(p => {
                  const bid = p.booking_id || p.id;
                  if (!bookingMap[bid]) {
                    bookingMap[bid] = {
                      booking_id: bid,
                      booking_ref: p.booking_ref,
                      client_name: p.client_name,
                      destination: p.destination,
                      travel_date: p.travel_date,
                      return_date: p.return_date,
                      pax_adults: p.pax_adults || 0,
                      pax_children: p.pax_children || 0,
                      nights: p.nights || 0,
                      total_amount: totalByBooking[bid] || Number(p.total_booking_amount || 0),
                      payments: [],
                    };
                  }
                  bookingMap[bid].payments.push(p);
                });

                const bookingGroups = Object.values(bookingMap);

                // Sort booking groups: most recent payment date first (DESC)
                bookingGroups.sort((a, b) => {
                  const latestA = Math.max(...a.payments.map(p => new Date(p.payment_date || p.created_at || 0).getTime()));
                  const latestB = Math.max(...b.payments.map(p => new Date(p.payment_date || p.created_at || 0).getTime()));
                  return latestB - latestA;
                });

                const statusColors = {
                  paid:    'bg-emerald-100 text-emerald-700',
                  partial: 'bg-amber-100 text-amber-700',
                  unpaid:  'bg-rose-100 text-rose-700',
                };

                return (
                  <div className="divide-y divide-gray-100">
                    {bookingGroups.map(bk => {
                      const paid   = paidByBooking[bk.booking_id] || 0;
                      const total  = bk.total_amount;
                      const bal    = Math.max(0, total - paid);
                      const status = bookingPaymentStatus(bk.booking_id);
                      const isOpen = expandedBookingId === bk.booking_id;
                      const paxStr = bk.pax_children > 0
                        ? `${bk.pax_adults}A + ${bk.pax_children}C`
                        : `${bk.pax_adults}A`;

                      // Payments sorted newest first for expanded view
                      const sortedPayments = [...bk.payments].sort((a, b) => {
                        const da = new Date(a.payment_date || a.created_at || 0).getTime();
                        const db = new Date(b.payment_date || b.created_at || 0).getTime();
                        return db - da; // DESC — most recent first
                      });

                      return (
                        <div key={bk.booking_id} data-testid={`booking-accordion-${bk.booking_id}`}>

                          {/* ── Collapsed Booking Row ───────────────────────── */}
                          <div
                            className="grid items-center px-5 py-4 bg-gray-50 hover:bg-emerald-50/30 cursor-pointer transition-colors"
                            style={{gridTemplateColumns:'minmax(0,1fr) 96px 96px 80px 24px', columnGap:'16px'}}
                            onClick={() => setExpandedBookingId(isOpen ? null : bk.booking_id)}
                          >
                            {/* 1: identity */}
                            <div className="min-w-0">
                              {/* Row 1: ref + status */}
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-semibold whitespace-nowrap">
                                  #{bk.booking_ref}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusColors[status] || 'bg-gray-100 text-gray-600'}`}>
                                  {status}
                                </span>
                              </div>
                              {/* Row 2: guest · destination · dates · pax */}
                              <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap">
                                {bk.client_name && (
                                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{bk.client_name}</span>
                                )}
                                {bk.destination && (
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {bk.destination}{bk.nights > 0 ? ` · ${bk.nights}N` : ''}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {formatDate(bk.travel_date)}{bk.return_date ? ` → ${formatDate(bk.return_date)}` : ''}
                                </span>
                                <span className="text-xs text-gray-400 whitespace-nowrap">{paxStr}</span>
                              </div>
                            </div>
                            {/* 2: Booking Amt */}
                            <div className="text-right">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-tight">Booking Amt</p>
                              <p className="font-bold text-gray-900 text-sm mt-0.5">{formatCurrency(total)}</p>
                            </div>
                            {/* 3: Received Amt */}
                            <div className="text-right">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-tight">Received Amt</p>
                              <p className="font-bold text-emerald-600 text-sm mt-0.5">{formatCurrency(paid)}</p>
                            </div>
                            {/* 4: Balance */}
                            <div className="text-right">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-tight">Balance</p>
                              <p className={`font-bold text-sm mt-0.5 ${bal > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {bal > 0 ? formatCurrency(bal) : 'Nil'}
                              </p>
                            </div>
                            {/* 5: Chevron */}
                            <div className="flex justify-end">
                              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400"/> : <ChevronDown className="w-4 h-4 text-gray-400"/>}
                            </div>
                          </div>

                          {/* ── Expanded Payment Transactions ───────────────── */}
                          {isOpen && (
                            <div className="bg-white border-t border-gray-100 px-5 pb-4 pt-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[580px]">
                                  <thead>
                                    <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                      <th className="py-2 pr-4 text-left  font-semibold w-[100px]">Date</th>
                                      <th className="py-2 pr-4 text-right font-semibold w-[110px]">Booking Amt</th>
                                      <th className="py-2 pr-4 text-right font-semibold w-[100px]">Received Amt</th>
                                      <th className="py-2 pr-4 text-right font-semibold w-[100px]">Balance Amt</th>
                                      <th className="py-2 pr-4 text-left  font-semibold w-[110px]">Mode</th>
                                      <th className="py-2       text-left  font-semibold">Transaction ID</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-50">
                                    {sortedPayments.map(pay => (
                                      <tr key={pay.id} className="hover:bg-gray-50">
                                        <td className="py-2.5 pr-4 text-gray-600 text-xs whitespace-nowrap">
                                          {formatDate(pay.payment_date)}
                                        </td>
                                        <td className="py-2.5 pr-4 text-right text-gray-700 font-semibold text-xs whitespace-nowrap">
                                          {formatCurrency(total)}
                                        </td>
                                        <td className="py-2.5 pr-4 text-right text-emerald-600 font-semibold text-xs whitespace-nowrap">
                                          {formatCurrency(pay.amount_received)}
                                        </td>
                                        <td className="py-2.5 pr-4 text-right font-semibold text-xs whitespace-nowrap">
                                          <span className={(balanceById[pay.id] || 0) > 0 ? 'text-red-500' : 'text-emerald-600'}>
                                            {(balanceById[pay.id] || 0) > 0 ? formatCurrency(balanceById[pay.id]) : 'Nil'}
                                          </span>
                                        </td>
                                        <td className="py-2.5 pr-4">
                                          <Badge className={getModeColor(pay.payment_mode)} variant="outline">
                                            {paymentModes[pay.payment_mode] || pay.payment_mode || '-'}
                                          </Badge>
                                        </td>
                                        <td className="py-2.5 text-gray-500 text-xs font-mono">
                                          {pay.transaction_id || '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AgentPayments;
